/** Admin review of items from the content pipeline. Wired into `api.admin.ingestReview` in api.ts. */

import { ingestAutoFixApi } from "./ingestAutoFixApi";
import { ingestChapterApi } from "./ingestChapterApi";

type RequestFn = <T>(path: string, options?: RequestInit, token?: string | null) => Promise<T>;

export type IngestStatus = "draft" | "published" | "rejected";

export type IngestItem = {
  id: string;
  course_id: string | null;
  semester: number | null;
  stream_id: string | null;
  university_id: string | null;
  external_id: string;
  kind: "module_topic" | "exam_question";
  resource_type: "note" | "quiz" | "past_exam" | "coc_exam";
  origin: "extracted" | "generated";
  status: IngestStatus;
  confidence: number;
  needs_review: boolean;
  review_reasons: string[];
  review_note: string | null;
  source: { file?: string; page_start?: number; page_end?: number } | null;
  published_ref: Record<string, unknown> | null;
  payload: Record<string, any>;
  updated_at: string | null;
};

export type IngestItemDetail = IngestItem & {
  review_history: { at: string; status: string; note: string | null; by: string }[];
  figure_data: Record<string, string>;
};

/** What the image policy decided for one picture. A discarded one can be restored while the item is a draft. */
export type FigureDecision = {
  figure_external_id: string;
  decision: "kept" | "discarded";
  reason: string;
  stage: "option" | "heuristic" | "reference" | "ai" | "fallback" | "admin" | "reapply";
  category: string | null;
  role: "question" | "option" | "solution" | "note";
  caption: string | null;
  page: number;
};

export type PossibleDuplicate = { id: string; title: string; is_published: boolean; ingested: boolean };

/** A group the server would not create on its own. Its items stay draft until the admin picks one of `options`. */
export type SlideError = { slide_id: string | null; topic_id: string | null; code: string; message: string };

export type BridgeBlock = {
  kind: "conflict" | "duplicate" | "refused" | "invalid_notes";
  reason: string;
  options: ("add_as_new_version" | "replace" | "allow_duplicate" | "cancel" | "auto_fix")[];
  item_ids: string[];
  possible_duplicates?: PossibleDuplicate[];
  /** invalid_notes only: readable problems per slide, and the chapter the Auto-fix runs on. */
  errors?: SlideError[];
  chapter_id?: string;
};

/** append (default): add to the chapter's draft. replace: rebuild the chapter draft (needs confirm_replace). new_chapter: put the items in a new chapter. */
export type ChapterMode = "append" | "replace" | "new_chapter";
export type ApproveOptions = { on_conflict?: "cancel" | "add_as_new_version" | "replace" | "append" | "new_chapter"; allow_duplicate?: boolean; confirm_replace?: boolean };

export type ApproveResult = IngestItem & { warnings: string[]; blocked: BridgeBlock[]; possible_duplicates: PossibleDuplicate[] };
export type BulkResult = { changed: number; unchanged: number; warnings: string[]; blocked: BridgeBlock[]; possible_duplicates: PossibleDuplicate[] };

export type IngestListQuery = {
  course_id?: string;
  status?: IngestStatus | "";
  needs_review?: boolean;
  resource_type?: string;
  sort?: "default" | "confidence_asc" | "confidence_desc" | "chapter";
  page?: number;
};

export const ingestReviewApi = (request: RequestFn) => ({
  ...ingestAutoFixApi(request),
  ...ingestChapterApi(request),
  list: (token: string, q: IngestListQuery) => {
    const p = new URLSearchParams();
    if (q.course_id) p.set("course_id", q.course_id);
    if (q.status) p.set("status", q.status);
    if (q.needs_review !== undefined) p.set("needs_review", String(q.needs_review));
    if (q.resource_type) p.set("resource_type", q.resource_type);
    if (q.sort) p.set("sort", q.sort);
    p.set("page", String(q.page ?? 1));
    return request<{ items: IngestItem[]; total: number; page: number; page_size: number }>(`/admin/ingest/review?${p}`, {}, token);
  },
  /** `discarded` also returns the images the policy left out (for the Discarded images panel). */
  get: (token: string, id: string, discarded = false) => request<IngestItemDetail>(`/admin/ingest/items/${id}${discarded ? "?discarded=true" : ""}`, {}, token),
  /** Remove a kept image or restore a discarded one (drafts only). The reply includes the discarded images. */
  figure: (token: string, id: string, figureId: string, action: "remove" | "restore", reason?: string) =>
    request<IngestItemDetail>(`/admin/ingest/items/${id}/figures/${figureId}`, { method: "POST", body: JSON.stringify({ action, reason }) }, token),
  patch: (token: string, id: string, body: { status?: IngestStatus; note?: string; edits?: Record<string, unknown> } & ApproveOptions) =>
    request<ApproveResult>(`/admin/ingest/items/${id}`, { method: "PATCH", body: JSON.stringify(body) }, token),
  bulk: (token: string, ids: string[], status: IngestStatus, note?: string, opts: ApproveOptions = {}) =>
    request<BulkResult>(
      "/admin/ingest/items/bulk-status",
      { method: "POST", body: JSON.stringify({ ids, status, note, ...opts }) },
      token,
    ),
  approveAll: (token: string, f: { status?: IngestStatus; resource_type?: string; course_id?: string; needs_review?: boolean } & ApproveOptions) =>
    request<{ approved: number; skipped: number; warnings: string[] }>(
      "/admin/ingest/items/approve-all",
      { method: "POST", body: JSON.stringify(f) },
      token,
    ),
  /** Ids of the past exams and COC exams created by ingestion, for the "Ingested" label in the admin lists. */
  created: (token: string) => request<{ past_exams: string[]; coc_exams: string[] }>("/admin/ingest/created", {}, token),
  /** Read-only: ingested exams whose free/premium flags differ from a manual upload. */
  accessAudit: (token: string) =>
    request<{ differs: { kind: string; id: string; title: string; is_published: boolean; is_free: boolean; access_level: string }[]; count: number; live: number }>(
      "/admin/ingest/access-audit",
      {},
      token,
    ),
});
